/**
 * What a board is like, in the terms that change how it should be played.
 *
 * A bot that only knows its own hand strength bets the same way on K-7-2 rainbow as
 * on 9-8-7 with two hearts, and those are different games. On the first, almost
 * nothing improves and a small bet does the whole job; on the second, half the deck
 * changes who is winning and a small bet is an invitation.
 *
 * These are descriptive features, computed from the cards. Nothing here is a strategy
 * — the strategy reads them.
 */
import { rankOf, suitOf } from "../pipeline/solve/evaluator";
import { categoryOf, hasStraight } from "./handclass";
import type { Card } from "./types";

export interface Texture {
  /** Board cards seen so far, 0, 3, 4 or 5. */
  street: 0 | 3 | 4 | 5;
  /** Highest board rank, 0..12, or -1 preflop. */
  high: number;
  /** Board is paired, tripled or double-paired. */
  paired: boolean;
  /** Three or more of one suit are out: a flush is possible or one card away. */
  monotone: boolean;
  twoTone: boolean;
  rainbow: boolean;
  /** The board itself already makes a straight. */
  straightOnBoard: boolean;
  /** A flush is already possible for someone. */
  flushPossible: boolean;
  /**
   * How much the board can still change, 0..1. Counts the cards that would make a
   * flush possible, complete a straight, or pair the board — a "wetness" that is
   * measured rather than asserted.
   */
  wetness: number;
  /** Share of the deck that improves nothing and changes nobody's hand. */
  blankFraction: number;
  /**
   * Connectivity, as the count of ranks that bring a straight within one further card
   * of somebody's reach. Zero on K-7-2, high on 9-8-7. A proxy, not a probability.
   */
  straightCards: number;
}

export function texture(board: readonly Card[]): Texture {
  if (board.length !== 0 && board.length !== 3 && board.length !== 4 && board.length !== 5) {
    throw new Error(`a board has 0, 3, 4 or 5 cards, not ${board.length}`);
  }
  const street = board.length as 0 | 3 | 4 | 5;
  if (street === 0) {
    return {
      street, high: -1, paired: false, monotone: false, twoTone: false, rainbow: true,
      straightOnBoard: false, flushPossible: false, wetness: 0, blankFraction: 1,
      straightCards: 0,
    };
  }

  const ranks = board.map(rankOf);
  const suits = board.map(suitOf);
  const high = Math.max(...ranks);

  const rc = new Int8Array(13);
  for (const r of ranks) rc[r] = rc[r]! + 1;
  const paired = ranks.length !== new Set(ranks).size;

  const sc = new Int8Array(4);
  for (const s of suits) sc[s] = sc[s]! + 1;
  const maxSuit = Math.max(...Array.from(sc));
  const monotone = maxSuit >= 3;
  const twoTone = maxSuit === 2 && new Set(suits).size < suits.length;
  const rainbow = maxSuit === 1;
  /**
   * Three of a suit means a flush is already makeable — with both hole cards on a
   * monotone flop, with one once a fourth arrives. Either way somebody can have it,
   * which is the only thing a betting decision needs to know.
   */
  const flushPossible = maxSuit >= 3;

  const boardMask = ranks.reduce((m, r) => m | (1 << r), 0);
  const straightOnBoard = categoryOf(board) === "straight" || categoryOf(board) === "straight_flush";

  /**
   * Connecting cards: ranks that would join at least two board ranks inside a single
   * five-card straight window.
   *
   * Stated as a window test rather than as "ranks that complete a straight for
   * somebody", because that question has no single answer — it depends how many cards
   * are still to come and how many the player holds, and the two earlier attempts got
   * it wrong in opposite directions. One allowed a single extra rank, so any board
   * needing both hole cards scored zero: T-9-2 and K-Q-2 both came back 0, which made
   * `rangeAdvantage` return the identical number for a broadway board and a dry one.
   * The other did not require the incoming card to be part of the straight, so on a
   * board already four to a straight every rank in the deck counted.
   *
   * What this measures is what the name says: how much of the deck brings a board
   * closer to a straight. K-7-2 has no two ranks inside any window, so nothing
   * connects it and the answer is zero. T-9-2 gets six. Nine-eight-seven gets five,
   * because three of the ranks that would extend it are already on the board.
   */
  const WINDOWS: number[][] = (() => {
    const w: number[][] = [];
    for (let lo = 0; lo <= 8; lo++) w.push([lo, lo + 1, lo + 2, lo + 3, lo + 4]);
    w.push([12, 0, 1, 2, 3]);          // the wheel, where the ace plays low
    return w;
  })();

  let straightCards = 0;
  if (!straightOnBoard) {
    for (let r = 0; r < 13; r++) {
      if (boardMask & (1 << r)) continue;
      const connects = WINDOWS.some(
        (win) => win.includes(r) && win.filter((x) => boardMask & (1 << x)).length >= 2);
      if (connects) straightCards++;
    }
  }

  /**
   * Wetness as the share of the remaining deck that changes the picture: any card
   * that pairs the board, brings a third or fourth card of a suit, or completes a
   * straight for a plausible holding.
   */
  const seen = new Set(board);
  let scary = 0, total = 0;
  for (let c = 0; c < 52; c++) {
    if (seen.has(c)) continue;
    total++;
    const r = rankOf(c), s = suitOf(c);
    const pairs = rc[r]! > 0;
    const suited = sc[s]! >= 2;          // brings a third of that suit, or a fourth
    /**
     * A card only "straightens" the board if the straight it makes uses it. Without
     * that test, a board already four to a straight flagged every card in the deck:
     * 7-6-5-4 came back with wetness exactly 1.000, and 13% of random rivers did the
     * same. `wetness` cuts estimated fold equity by up to 30%, so the bots treated
     * those boards as unbluffable.
     */
    const straightens = !straightOnBoard && (() => {
      for (let o = 0; o < 13; o++) {
        if (o === r) continue;
        const withBoth = boardMask | (1 << r) | (1 << o);
        if (hasStraight(withBoth) && !hasStraight(withBoth & ~(1 << r))) return true;
      }
      return false;
    })();
    if (pairs || suited || straightens) scary++;
  }
  const wetness = total > 0 ? scary / total : 0;

  return {
    street, high, paired, monotone, twoTone, rainbow,
    straightOnBoard, flushPossible, wetness,
    blankFraction: 1 - wetness, straightCards,
  };
}

/**
 * How much the board favours the preflop aggressor's range over the caller's.
 *
 * Positive means the raiser's range connects better — high, disconnected boards, where
 * the raiser holds far more of the top pairs and overpairs. Negative means the caller
 * does — low connected boards the raiser mostly missed. This is the single number
 * behind "should I continuation-bet here", and getting its SIGN right matters far more
 * than getting its magnitude right.
 */
export function rangeAdvantage(t: Texture): number {
  if (t.street === 0) return 0;
  // High cards favour the raiser: an ace or king high board is the clearest case.
  const highPull = (t.high - 6) / 6;                 // -1 at deuce-high, +1 at ace-high
  // Connectivity and suits favour the caller, who holds the suited and connected hands
  // the raiser folded or would have raised bigger with.
  const connectPull = -(t.straightCards / 13) * 0.9 - (t.monotone ? 0.35 : t.twoTone ? 0.15 : 0);
  // A paired board is close to neutral but slightly favours the raiser's overpairs.
  const pairPull = t.paired ? 0.1 : 0;
  return Math.max(-1, Math.min(1, highPull * 0.7 + connectPull + pairPull));
}
